import { Typography, TextField, Autocomplete, Checkbox } from '@scale/scaleui';
import {
  ResumeScreeningConfig,
  EditGreenhouseTagsAction,
  GreenhouseActionType,
  MoveStageGreenhouseAction,
  GreenhouseAction,
  NextResumeScreeningAction,
} from '@scale/shared-types';
import React, { useState, useEffect, useMemo } from 'react';
import { ResumeScreeningAction, actionTypeMappping } from '../constants';
import _ from 'lodash';

const getTagsFromActions = (actions: GreenhouseAction[]) => {
  const editTagActions = actions?.filter(
    action => action.type === GreenhouseActionType.EditTags,
  ) as EditGreenhouseTagsAction[];

  return {
    tagsToAdd: editTagActions.map(action => action.tagsToAdd).join(', '),
    tagsToRemove: editTagActions.map(action => action.tagsToRemove).join(', '),
  };
};

const getDestinationStageNameFromActions = (
  actions: GreenhouseAction[],
  jobStages: Record<string, string>,
) => {
  const moveStageAction = actions?.find(
    action => action.type === GreenhouseActionType.MoveStage,
  ) as MoveStageGreenhouseAction;

  const destinationStageId = moveStageAction?.toStageId;
  return jobStages && destinationStageId ? jobStages[destinationStageId] : '';
};

type CheckedState = {
  moveStages: boolean;
  editTags: boolean;
  nextResumeScreen: boolean;
};

export const ScreenActions: React.FC<{
  actionType: ResumeScreeningAction;
  selectedJobConfig: ResumeScreeningConfig;
  jobStages: Record<string, string>;
  loadingJobStages: boolean;
  handleActionUpdate: (
    actionCategory: ResumeScreeningAction,
    actionType: GreenhouseActionType,
    newAction: GreenhouseAction,
  ) => void;
  handleRemoveAction: (
    actionCategory: ResumeScreeningAction,
    actionType: GreenhouseActionType,
  ) => void;
  allJobConfigs?: ResumeScreeningConfig[];
  validChainableConfigs?: ResumeScreeningConfig[];
}> = ({
  actionType,
  selectedJobConfig,
  jobStages,
  loadingJobStages,
  handleActionUpdate,
  handleRemoveAction,
  allJobConfigs,
  validChainableConfigs,
}) => {
  const [checkedStates, setCheckedStates] = useState<{
    moveStages: boolean;
    editTags: boolean;
    nextResumeScreen: boolean;
  }>({
    moveStages: false,
    editTags: false,
    nextResumeScreen: false,
  });
  const [moveStageDestination, setMoveStageDestination] = useState('');
  const [nextResumeScreenName, setNextResumeScreenName] = useState('');
  const [tagsToAdd, setTagsToAdd] = useState('');
  const [tagsToRemove, setTagsToRemove] = useState('');

  const displayJobStages: Record<string, string> = useMemo(() => {
    return jobStages
      ? Object.keys(jobStages).reduce((newJobStages, id) => {
          newJobStages[id] = `${jobStages[id]} (${id})`;
          return newJobStages;
        }, {} as Record<string, string>)
      : {};
  }, [jobStages]);

  useEffect(() => {
    const actions = selectedJobConfig?.[actionTypeMappping[actionType]] as GreenhouseAction[];
    setCheckedStates({
      moveStages: !!actions?.find(
        (action: GreenhouseAction) =>
          action.type === GreenhouseActionType.MoveStage &&
          (action as MoveStageGreenhouseAction).toStageId,
      ),
      editTags: !!actions?.find(
        (action: GreenhouseAction) =>
          action.type === GreenhouseActionType.EditTags &&
          (!_.isEmpty((action as EditGreenhouseTagsAction).tagsToAdd) ||
            !_.isEmpty((action as EditGreenhouseTagsAction).tagsToRemove)),
      ),
      nextResumeScreen: !!actions?.find(
        (action: GreenhouseAction) =>
          action.type === GreenhouseActionType.NextResumeScreening &&
          (action as NextResumeScreeningAction).nextResumeScreeningConfigId,
      ),
    });
  }, [selectedJobConfig, actionType]);

  useEffect(() => {
    if (allJobConfigs) {
      const nextResumeScreeningAction = selectedJobConfig?.onFailActions?.find(
        action => action.type === GreenhouseActionType.NextResumeScreening,
      ) as NextResumeScreeningAction;

      if (nextResumeScreeningAction) {
        const nextResumeScreenName = allJobConfigs.find(
          config => config._id === nextResumeScreeningAction.nextResumeScreeningConfigId,
        )?.name;
        setNextResumeScreenName(nextResumeScreenName || '');
      }
    }
  }, [allJobConfigs, selectedJobConfig]);

  useEffect(() => {
    const destinationStageName = getDestinationStageNameFromActions(
      (selectedJobConfig?.[actionTypeMappping[actionType]] as GreenhouseAction[]) || [],
      displayJobStages,
    );
    setMoveStageDestination(destinationStageName);
  }, [selectedJobConfig, displayJobStages, actionType]);

  useEffect(() => {
    const tags = getTagsFromActions(
      (selectedJobConfig?.[actionTypeMappping[actionType]] as GreenhouseAction[]) || [],
    );
    setTagsToAdd(tags.tagsToAdd);
    setTagsToRemove(tags.tagsToRemove);
  }, [selectedJobConfig._id]);

  const toggleCheckbox = (key: keyof CheckedState) => {
    setCheckedStates(prevState => {
      const isChecked = !prevState[key];
      let actionTypeToRemove: GreenhouseActionType | undefined;

      if (key === 'moveStages') {
        actionTypeToRemove = GreenhouseActionType.MoveStage;
      } else if (key === 'editTags') {
        actionTypeToRemove = GreenhouseActionType.EditTags;
      } else if (key === 'nextResumeScreen') {
        actionTypeToRemove = GreenhouseActionType.NextResumeScreening;
      }

      // Remove the action if the checkbox is unchecked
      if (!isChecked && actionTypeToRemove) {
        handleRemoveAction(actionType, actionTypeToRemove);
        if (actionTypeToRemove === GreenhouseActionType.MoveStage) {
          setMoveStageDestination('');
        } else if (actionTypeToRemove === GreenhouseActionType.EditTags) {
          setTagsToAdd('');
          setTagsToRemove('');
        } else if (actionTypeToRemove === GreenhouseActionType.NextResumeScreening) {
          setNextResumeScreenName('');
        }
      }

      return {
        ...prevState,
        [key]: isChecked,
      };
    });
  };

  const handleEditTagsUpdate = (key: 'tagsToAdd' | 'tagsToRemove', value: string) => {
    if (key === 'tagsToAdd') {
      setTagsToAdd(value);
    } else {
      setTagsToRemove(value);
    }

    const actions = selectedJobConfig?.[actionTypeMappping[actionType]] as GreenhouseAction[];
    const editTagAction = actions?.find(
      action => action.type === GreenhouseActionType.EditTags,
    ) as EditGreenhouseTagsAction;
    const newTags = value.split(',').map(tag => tag.trim());

    const updatedEditTagsAction: EditGreenhouseTagsAction = {
      type: GreenhouseActionType.EditTags,
      tagsToAdd: key === 'tagsToAdd' ? newTags : editTagAction?.tagsToAdd || [],
      tagsToRemove: key === 'tagsToRemove' ? newTags : editTagAction?.tagsToRemove || [],
    };

    handleActionUpdate(actionType, GreenhouseActionType.EditTags, updatedEditTagsAction);
  };

  const handleMoveStageDestinationAutocompleteChange = (selectedOption: string) => {
    const stageId = Object.keys(displayJobStages).find(
      key => displayJobStages[key] === selectedOption,
    );
    if (stageId) {
      const newMoveStageAction: MoveStageGreenhouseAction = {
        type: GreenhouseActionType.MoveStage,
        fromStageId: selectedJobConfig.resumeReviewStageId,
        toStageId: stageId,
      };

      handleActionUpdate(actionType, GreenhouseActionType.MoveStage, newMoveStageAction);
    }
  };

  const handleNextResumeScreenUpdate = (selectedName: string) => {
    const nextResumeScreenConfig = allJobConfigs?.find(config => config.name === selectedName);
    if (nextResumeScreenConfig) {
      const newNextResumeScreenAction: NextResumeScreeningAction = {
        type: GreenhouseActionType.NextResumeScreening,
        nextResumeScreeningConfigId: nextResumeScreenConfig._id,
      };

      handleActionUpdate(
        actionType,
        GreenhouseActionType.NextResumeScreening,
        newNextResumeScreenAction,
      );
    }
  };

  const actionTypeMessages = {
    [ResumeScreeningAction.Pass]:
      'Select the actions to take when the candidate passes the LLM screen:',
    [ResumeScreeningAction.NeedsHumanReview]:
      'Select the actions to take when the candidate gets flagged for human review:',
    [ResumeScreeningAction.Fail]:
      'Select the actions to take when the candidate fails the LLM screen:',
  };

  return (
    <div className="scaleui scale-theme scale-tailwind p-2">
      <Typography.H4 className="mb-4 text-neutral-800">
        {actionType === ResumeScreeningAction.Pass
          ? 'Pass Actions'
          : actionType === ResumeScreeningAction.NeedsHumanReview
          ? 'Needs Human Review Actions'
          : 'Fail Actions'}
      </Typography.H4>
      <div className="flex flex-col space-y-3">
        <div className="text-neutral-900 mb-2">
          {actionTypeMessages[actionType]}
          {actionType === ResumeScreeningAction.Fail && (
            <div className="text-neutral-500 text-xs mt-2">
              Note: Fail actions must include either a Move Stage action or a Next Resume Screen
              action, but not both.
            </div>
          )}
        </div>
        <div className="flex flex-row gap-2">
          <Checkbox
            checked={checkedStates.moveStages}
            onChange={() => toggleCheckbox('moveStages')}
          />
          <Typography.Paragraph2
            className={checkedStates.moveStages ? 'text-neutral-900' : 'text-neutral-500'}
          >
            Move Stages{' '}
            {(actionType === ResumeScreeningAction.Pass ||
              actionType === ResumeScreeningAction.NeedsHumanReview) &&
              '[Required]'}
          </Typography.Paragraph2>
        </div>
        {checkedStates.moveStages && (
          <Autocomplete
            className="w-full h-auto text-neutral-900"
            canCreateOption={false}
            options={Object.values(displayJobStages)}
            value={loadingJobStages ? 'Loading...' : moveStageDestination}
            setSelectedOption={handleMoveStageDestinationAutocompleteChange}
            disabled={!checkedStates.moveStages || loadingJobStages}
            trailingIcon={'chevron-down'}
          />
        )}
        <div className="flex flex-row gap-2">
          <Checkbox checked={checkedStates.editTags} onChange={() => toggleCheckbox('editTags')} />
          <Typography.Paragraph2
            className={checkedStates.editTags ? 'text-neutral-900' : 'text-neutral-500'}
          >
            Edit Tags
          </Typography.Paragraph2>
        </div>
        {checkedStates.editTags && (
          <>
            <div className="text-neutral-500 text-xs">Tags must already exist in Greenhouse.</div>
            <TextField
              className="text-neutral-900"
              disabled={!checkedStates.editTags}
              label="Tags to Add"
              value={tagsToAdd}
              onChange={e => handleEditTagsUpdate('tagsToAdd', e.target.value)}
            />
            <TextField
              className="text-neutral-900"
              disabled={!checkedStates.editTags}
              label="Tags to Remove"
              value={tagsToRemove}
              onChange={e => handleEditTagsUpdate('tagsToRemove', e.target.value)}
            />
          </>
        )}
        {actionType === ResumeScreeningAction.Fail && (
          <>
            <div className="flex flex-row gap-2">
              <Checkbox
                checked={checkedStates.nextResumeScreen}
                onChange={() => toggleCheckbox('nextResumeScreen')}
              />
              <Typography.Paragraph2
                className={checkedStates.nextResumeScreen ? 'text-neutral-900' : 'text-neutral-500'}
              >
                Next Resume Screen
              </Typography.Paragraph2>
            </div>
            {checkedStates.nextResumeScreen && (
              <div>
                <div className="text-neutral-500 text-xs mb-4">
                  A chained resume screen will only be executed if the parent screening(s) have all
                  failed.
                </div>
                <Autocomplete
                  className="w-full h-auto"
                  canCreateOption={false}
                  options={
                    validChainableConfigs ? validChainableConfigs.map(config => config.name) : []
                  }
                  value={checkedStates.nextResumeScreen ? nextResumeScreenName : ''}
                  setSelectedOption={handleNextResumeScreenUpdate}
                  disabled={!checkedStates.nextResumeScreen}
                  trailingIcon={'chevron-down'}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
